<template>
  <section class="privacy-policy">
    <article class="content-wrapper">
      <h3 class="title">Privacy Policy</h3>
      <section class="description">
        <p>
          Welcome to SKYBOX ("SKYBOX," "we," "us" or "our"). SKYBOX is an easy
          to use, powerful multi-platform VR player.
        </p>
        <p>
          Our Privacy Policy explains how we collect, use, share and protect
          information in relation to our software and services (collectively,
          the "Service"), and your choices about the collection and use of your
          information.
        </p>
        <p>
          Our Policy applies to all users and others who access the Service
          ("Users"). Your use of (or request to use) SKYBOX or the Service
          constitutes your acceptance of this Policy. This Policy is effective
          on and from January 24 2017 (last updated December 15 2021).
        </p>
        <h4 class="headline">1. INFORMATION WE COLLECT OR HAVE ACCESS TO</h4>
        <div class="sub-description">
          <p>
            We may collect or have access to the following types of information.
          </p>
          <h5 class="sub-headline">Information you provide us directly:</h5>
          <p>
            Communications between you and Skybox. For example, you may be
            emailing us for tests or updates, and we may be responding to your
            emails to provide you with our Service updates or notices. In these
            cases, we may have your email address.
          </p>
          <p>
            Contact information you decide to voluntarily provide us under
            certain circumstances for your benefit. For example, you may be
            sending us your phone number for us to call to solve your problems
            in using our Service.
          </p>
          <h5 class="sub-headline">Analytics information:</h5>
          <p>
            We use third-party analytics tools to help us measure traffic and
            usage trends for the Service. These tools collect information sent
            by your device or our Service, such as the web pages you visit,
            add-ons, and other information that assists us in delivering and
            improving the Service. We collect and use this analytics information
            with analytics information from other Users in an aggregated manner
            so that it cannot reasonably be used to identify any particular
            individual User.
          </p>
          <h5 class="sub-headline">Log file information:</h5>
          <p>
            Log file information may be provided to us in either of the
            following ways: (i) when you use a testing version of our Service,
            your log file information will be automatically reported to us when
            you meet a technical problem in your use, where we will use the log
            file information to identify, analyze and resolve the problem for
            you; or (ii) when you use a formal purchased version of our Service
            and contact us when you meet a technical problem, we may ask you to
            provide your log file information for us to test and debug, in which
            case you can decide whether to provide us with the log file
            information or not.
          </p>
          <h5 class="sub-headline">Device information:</h5>
          <p>
            When you use a VR device to access our Service, certain information
            about your device may be included in the log file information you
            provide us, such as device type and other hardware information.
          </p>
        </div>
        <h4 class="headline">2. HOW WE USE YOUR INFORMATION</h4>
        <div class="sub-description">
          <p>We use the above information to:</p>
          <p>
            √ Provide and improve our Service (e.g., debug and trouble-shooting)
            <br />
            √ Develop and test new products and features <br />
            √ Monitor metrics such as total number of visitors, traffic, and
            demographic patterns <br />
            √ Diagnose or fix technology problems
          </p>
          <p>
            We will not rent or sell your information to third parties without
            your consent.
          </p>
        </div>
        <h4 class="headline">3. HOW WE SHARE YOUR INFORMATION</h4>
        <div class="sub-description">
          <p>
            We may share your information among our affiliates to direct your
            questions or technical problems to the right team for more
            satisfactory solution. Other than this and other than to comply with
            applicable laws, we do not share your information with third parties
            without your consent.
          </p>
        </div>
        <h4 class="headline">4. HOW WE STORE YOUR INFORMATION</h4>
        <div class="sub-description">
          <p>
            The information collected through the Service may be stored and
            processed in countries where SKYBOX and its affiliates maintain
            teams and facilities. The information may be in a country or
            jurisdiction different from your own, which may not have the same
            data protection laws as your own country or jurisdiction. We use
            commercially reasonable safeguards to keep the personal information
            you provide us for the Service secure.
          </p>
          <p>
            By using the Service and/or contacting us, you consent to the
            access, use and storage of information as described above and
            elsewhere in this Privacy Policy.
          </p>
        </div>
        <h4 class="headline">5. DELETION OF YOUR INFORMATION</h4>
        <div class="sub-description">
          <p>
            We will delete your information you provide to us after we have
            solved your technical problems, or will otherwise only keep such
            information to the minimum extent as required to provide and improve
            our Service to you. You can also contact us (see “How to Contact Us”
            below) to have your information deleted.
          </p>
        </div>
        <h4 class="headline">6. RIGHTS YOU HAVE OVER YOUR DATA</h4>
        <div class="sub-description">
          <p>
            If your personal information is collected from you and where
            applicable laws apply, you will have the right to request for
            access, rectification, erasure, processing restriction and
            portability of your personal data, and the right to lodge a
            complaint with a data protection supervisory authority.
          </p>
        </div>
        <h4 class="headline">7. CHILDREN’S PRIVACY</h4>
        <div class="sub-description">
          <p>
            In the event that we learn that we have collected personal
            information from a child under age 13 without parental consent, we
            will delete that information as quickly as possible. If you believe
            that we might have any information from or about a child under 13,
            please contact us.
          </p>
        </div>
        <h4 class="headline">8. OTHER WEB-SITES AND SERVICES</h4>
        <div class="sub-description">
          <p>
            We are not responsible for the practices employed by any websites or
            services linked to or from our Service, including the information or
            content contained within them. Please remember that when you use a
            link to access our Service or to go from our Service to another
            website or service, our Privacy Policy does not apply to those
            third-party websites or services.
          </p>
          <p>
            Your browsing and interaction on any third-party website or service,
            including those that have a link on our website, are subject to that
            third party’s own rules and policies. In addition, you agree that we
            are not responsible and do not have control over any third-party
            websites or services or their data & privacy practices.
          </p>
        </div>
        <h4 class="headline">9. CALIFORNIA REQUIREMENTS</h4>
        <div class="sub-description">
          <p>
            The California Consumer Privacy Act (CCPA) requires specific
            disclosures for California residents. To the extent CCPA applies,
            this section contains these specific disclosures.
          </p>
          <div>
            <p>
              <span class="underline">
                Categories of personal information we collect:</span
              >
              email address or other personal information that you voluntarily
              provide to us in your direct communication with us in relation to
              the Service.
            </p>
          </div>

          <p>
            <span class="underline">
              Business purposes for which we may have your personal
              information:</span
            >
            for you to contact us to use our Service or for troubleshooting.
          </p>
          <div>
            <p>
              <span class="underline">
                Parties with whom personal information may be shared:</span
              >

              Parties with whom personal information may be shared: We may share
              your information among our affiliates to direct your questions or
              technical problems to the right team for more satisfactory
              solution. Other than this and other than to comply with applicable
              laws, we do not share your information with third parties without
              your consent.
            </p>
          </div>

          <p>
            CCPA provides you with the right to access, modify and delete your
            personal information as well as the right to request information
            about how we collect and use your personal information. We do not
            sell your personal information. We will not discriminate against you
            for exercising your privacy rights under CCPA. If you have questions
            or requests related to your rights under the CCPA, you can contact
            us.
          </p>
        </div>
        <h4 class="headline">10. HOW TO CONTACT US</h4>
        <div class="sub-description">
          <p>
            If you have any questions about this Privacy Policy or if you want
            to request deletion of any personal information you have provided to
            us, please contact us at
            <a href="mailto:contact@SKYBOX.xyz" data-v-afcfcc00=""
              >contact@SKYBOX.xyz</a
            >.
          </p>
        </div>
        <h4 class="headline">11. CHANGES TO OUR PRIVACY POLICY</h4>
        <div class="sub-description">
          <p>
            We may modify or update this Privacy Policy from time to time, so
            please review it periodically. We may provide you notice of
            modifications or updates if required by applicable laws. Your
            continued use of (or request to use) SKYBOX or the Service after any
            modification to this Privacy Policy will constitute your acceptance
            of such modification.
          </p>
        </div>
      </section>
    </article>
  </section>
</template>
<style lang="scss" scoped>
@import "../assets/scss/privacy.scss";
.underline {
  text-decoration: underline;
}
</style>